import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  appSyncGraphQLApi,
  useGetSharedBoardsByIDsQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetHealthProBoardsQuery,
  useGetSharedContentBoardsForEndUserQuery,
  useGetSharedProgrammesForEndUserQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';
import usePrevious from '../../services/use_previous';

const invalidateHealthProMealsQueriesAction = (healthProBoardsIDs) => {
  return (dispatch) => {
    // const getSharedMealsQueriesPotentiallyAffected =
    //   appSyncGraphQLApi.util.selectInvalidatedBy(getState(), [
    //     'DistilledMealsByParent',
    //     'FullMealsByParent',
    //   ]);
    // const getSharedMealsQueriesToInvalidate =
    //   getSharedMealsQueriesPotentiallyAffected.filter(
    //     ({ endpointName, originalArgs }) => {
    //       if (
    //         ['getDistilledMealsByParent', 'getFullMealsByParent'].includes(
    //           endpointName,
    //         )
    //       ) {
    //         return healthProBoardsIDs.includes(originalArgs);
    //       }
    //       if (
    //         ['getDistilledMealsByParents', 'getFullMealsByParents'].includes(
    //           endpointName,
    //         )
    //       ) {
    //         return originalArgs.some((parentID) =>
    //           healthProBoardsIDs.includes(parentID),
    //         );
    //       }
    //       return false;
    //     },
    //   );
    const tagsToInvalidate = healthProBoardsIDs.flatMap((parentID) => [
      { type: 'DistilledMealsByParent', id: parentID },
      { type: 'FullMealsByParent', id: parentID },
    ]);
    console.log(tagsToInvalidate);
    dispatch(appSyncGraphQLApi.util.invalidateTags(tagsToInvalidate));
  };
};

const invalidateSharedMealsQueriesAction = (sharedBoardIDs) => {
  return (dispatch) => {
    // const getHealthProMealsPotentiallyAffected =
    //   appSyncGraphQLApi.util.selectInvalidatedBy(getState(), [
    //     'DistilledSharedMealsByParent',
    //   ]);
    // const getHealthProMealsQueriesToInvalidate =
    //   getHealthProMealsPotentiallyAffected.filter(
    //     ({ endpointName, originalArgs }) => {
    //       if (endpointName === 'getDistilledSharedMealsByParent') {
    //         return sharedBoardIDs.includes(originalArgs);
    //       }
    //       if (endpointName === 'getDistilledSharedMealsByParents') {
    //         return originalArgs.some((parentID) =>
    //           sharedBoardIDs.includes(parentID),
    //         );
    //       }
    //       return false;
    //     },
    //   );
    const tagsToInvalidate = sharedBoardIDs.flatMap((parentID) => [
      { type: 'DistilledSharedMealsByParent', id: parentID },
    ]);
    console.log(tagsToInvalidate);
    dispatch(appSyncGraphQLApi.util.invalidateTags(tagsToInvalidate));
  };
};

const invalidateSharedContentEntriesQueriesAction = (parentIDs) => {
  return (dispatch) => {
    const tagsToInvalidate = parentIDs.flatMap((parentID) => [
      { type: 'SharedContentEntriesByParent', id: parentID },
    ]);
    console.log(tagsToInvalidate);
    dispatch(appSyncGraphQLApi.util.invalidateTags(tagsToInvalidate));
  };
};

/**
 * In the end user app, the following resources are automatically refetched, by passing
 * `refetchOnMountOrArgChange` to the query hook:
 * - the public Space.
 *.     if space changes then shared boards list changes, so the list of shared boards is refetched
 * - SharedBoard list and each individual SharedBoard
 * - SharedProgramme list and each individual SharedProgramme.
 *      if an individual shared programme changes, does it refetch the list of health pro boards?
 *      yes, the boards are referenced by ID
 *
 * Note that refetching happens on user action, not automatically in the background.
 *
 * We DO NOT regularly refetch list of meals/shared meals, takes a long time.
 *
 * if an individual shared board changes, the list of meals needs to be manually
 * invalidated because it is fetched by parent ID. The same applies to each health pro board
 * associated with the currently enrolled shared programme.
 * The following RTK Query tags are affected:
 *         { type: 'DistilledMealsByParent', id: parentID },
 *         { type: 'FullMealsByParent', id: parentID },
 *         { type: 'DistilledSharedMealsByParent', id: parentID },
 *
 * shared programme also has sharedContentEntries as parents
 * shared programme also has shared content entry references to other boards
 *
 */
const EndUserPublishedResourcesControl = () => {
  const dispatch = useDispatch();

  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedRecipesBoardsIDs,
    {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const stringifiedSharedBoards = JSON.stringify(sharedBoards);

  const prevStringifiedSharedBoards = usePrevious(stringifiedSharedBoards);

  const sharedBoardIDs = useMemo(
    () => (sharedBoards ? sharedBoards.map((board) => board.id) : []),
    [sharedBoards],
  );

  useEffect(() => {
    if (
      prevStringifiedSharedBoards &&
      prevStringifiedSharedBoards !== stringifiedSharedBoards
    ) {
      console.warn(
        `Shared boards list has changed, Need to refetch shared meals`,
      );
      dispatch(invalidateSharedMealsQueriesAction(sharedBoardIDs));
    }
  }, [
    dispatch,
    prevStringifiedSharedBoards,
    sharedBoardIDs,
    stringifiedSharedBoards,
  ]);

  const { data: healthProBoards } = useGetHealthProBoardsQuery();

  const stringifiedHealthProBoards = JSON.stringify(healthProBoards);

  const prevStringifiedHealthProBoards = usePrevious(
    stringifiedHealthProBoards,
  );

  const healthProBoardsIDs = useMemo(
    () => (healthProBoards ? healthProBoards.map((board) => board.id) : []),
    [healthProBoards],
  );

  useEffect(() => {
    if (
      prevStringifiedHealthProBoards &&
      stringifiedHealthProBoards !== prevStringifiedHealthProBoards
    ) {
      console.warn(
        `Health pro boards have changed, Need to refetch health pro meals`,
      );
      dispatch(invalidateHealthProMealsQueriesAction(healthProBoardsIDs));
    }
  }, [
    dispatch,
    healthProBoardsIDs,
    prevStringifiedHealthProBoards,
    stringifiedHealthProBoards,
  ]);

  const { data: sharedContentBoards } =
    useGetSharedContentBoardsForEndUserQuery();

  const stringifiedSharedContentBoards = JSON.stringify(sharedContentBoards);

  const prevStringifiedSharedContentBoards = usePrevious(
    stringifiedSharedContentBoards,
  );

  const sharedContentBoardIDs = useMemo(
    () =>
      sharedContentBoards ? sharedContentBoards.map((board) => board.id) : [],
    [sharedContentBoards],
  );

  useEffect(() => {
    if (
      prevStringifiedSharedContentBoards &&
      stringifiedSharedContentBoards !== prevStringifiedSharedContentBoards
    ) {
      console.warn(
        `Shared content boards have changed, Need to refetch shared content entries`,
      );
      dispatch(
        invalidateSharedContentEntriesQueriesAction(sharedContentBoardIDs),
      );
    }
  }, [
    dispatch,
    prevStringifiedSharedContentBoards,
    sharedContentBoardIDs,
    stringifiedSharedContentBoards,
  ]);

  const { data: allSharedProgrammes } = useGetSharedProgrammesForEndUserQuery();

  const stringifiedSharedProgrammes = JSON.stringify(allSharedProgrammes);

  const prevStringifiedSharedProgrammes = usePrevious(
    stringifiedSharedProgrammes,
  );

  const sharedProgrammeIDs = useMemo(
    () => (allSharedProgrammes ? allSharedProgrammes.map((pr) => pr.id) : []),
    [allSharedProgrammes],
  );

  useEffect(() => {
    if (
      prevStringifiedSharedProgrammes &&
      stringifiedSharedProgrammes !== prevStringifiedSharedProgrammes
    ) {
      console.warn(
        `Shared programmes have changed, Need to refetch shared content entries`,
      );
      dispatch(invalidateSharedContentEntriesQueriesAction(sharedProgrammeIDs));
    }
  }, [
    dispatch,
    prevStringifiedSharedProgrammes,
    sharedProgrammeIDs,
    stringifiedSharedProgrammes,
  ]);

  return '';
};

export default EndUserPublishedResourcesControl;
